/**
 * The PostHog ids the landing page handed over on the link, taken off the URL before anything
 * else can read it.
 *
 * `www.flowgate.co.uk` and `app.flowgate.co.uk` are separate origins and FlowGate sets no cookie,
 * so nothing crosses on its own: without this a visitor who clicked "Build it" arrived as a brand
 * new anonymous person and no funnel could span the two sites. `landing-page/src/analytics.js`
 * puts its distinct id and session id on the link (`handoffParams` + `appUrl`), and
 * `initPostHog.js` boots PostHog with them, PostHog's documented way to continue a person and a
 * session across a domain.
 *
 * <p><strong>This module reads and strips at import time, on purpose.</strong> `App.js` calls
 * `createBrowserRouter` at module scope, which snapshots `window.location` the moment it is
 * imported. Anything stripped later is stripped only from the address bar: the router still holds
 * the original query and writes it back the next time a page calls `setSearchParams`, which is
 * how `fg_did` reappeared in the URL after the builder added `screen=`. So this has to run before
 * `./App` is evaluated, and `index.js` imports it first for that reason, the import order there
 * is load-bearing, not cosmetic.
 *
 * The ids are removed rather than left in place because an analytics id in a shareable address is
 * a small but real way for one person's identity to be attached to somebody else's visit.
 */

/**
 * Reads `fg_did` / `fg_sid` from a query string and removes them from the current URL.
 *
 * Runs whether or not analytics is switched on: a clean address bar is not the analytics layer's
 * business, and leaving the ids for an internal-traffic browser would leave them in the URL of
 * every screenshot we take.
 *
 * @param {string} [search=window.location.search] Injectable for tests.
 * @returns {{distinctID?: string, isIdentifiedID?: boolean, sessionID?: string}|undefined} the
 *   PostHog `bootstrap` for this page load, or undefined when the link carried nothing.
 */
export function captureHandoff(search = (typeof window !== 'undefined' ? window.location.search : '')) {
  if (typeof window === 'undefined') {
    return undefined;
  }
  try {
    const params = new URLSearchParams(search);
    const distinctID = params.get('fg_did');
    const sessionID = params.get('fg_sid');
    if (!distinctID && !sessionID) {
      return undefined;
    }
    params.delete('fg_did');
    params.delete('fg_sid');
    const query = params.toString();
    window.history.replaceState(window.history.state, '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    const bootstrap = {};
    if (distinctID) {
      // Still anonymous, just the SAME anonymous visitor. Marking it identified would mint a
      // person profile for everyone who clicks a link, which person_profiles: 'identified_only'
      // exists to avoid.
      bootstrap.distinctID = distinctID;
      bootstrap.isIdentifiedID = false;
    }
    if (sessionID) {
      bootstrap.sessionID = sessionID;
    }
    return bootstrap;
  } catch {
    return undefined;
  }
}

let captured = captureHandoff();

/** The `bootstrap` to hand `posthog.init`, or undefined for a visitor who arrived directly. */
export function handoffBootstrap() {
  return captured;
}

/** Test-only: re-reads from the given query string, as though this were a fresh page load. */
export function _recaptureForTests(search) {
  captured = captureHandoff(search);
  return captured;
}
